// Deterministic scenarios for UI development and tests. Enabled only on local hosts via ?mock=<scenario>.
import {
  MeasurementError,
  type EngineRaw,
  type MeasurementProvider,
  type RawMeasurementResult,
  type TestOptions,
  type TestStage,
} from './types';

interface Scenario {
  downloadMbps: number;
  uploadMbps: number;
  latencyMs: number;
  jitterMs: number;
  loadedLatencyMs: number | null;
}

export const MOCK_SCENARIOS = {
  excellent: { downloadMbps: 356, uploadMbps: 371, latencyMs: 12, jitterMs: 2, loadedLatencyMs: 28 },
  'busy-latency': { downloadMbps: 356, uploadMbps: 371, latencyMs: 18, jitterMs: 4, loadedLatencyMs: 310 },
  'slow-download': { downloadMbps: 3.2, uploadMbps: 2.5, latencyMs: 45, jitterMs: 6, loadedLatencyMs: 140 },
  'slow-upload': { downloadMbps: 120, uploadMbps: 1.6, latencyMs: 22, jitterMs: 3, loadedLatencyMs: 60 },
  'high-latency': { downloadMbps: 80, uploadMbps: 12, latencyMs: 210, jitterMs: 12, loadedLatencyMs: 260 },
  unstable: { downloadMbps: 95, uploadMbps: 18, latencyMs: 35, jitterMs: 38, loadedLatencyMs: 90 },
  'no-loaded': { downloadMbps: 150, uploadMbps: 40, latencyMs: 20, jitterMs: 3, loadedLatencyMs: null },
} satisfies Record<string, Scenario>;

export type MockScenarioName = keyof typeof MOCK_SCENARIOS | 'fail';

export function isMockScenario(name: string): name is MockScenarioName {
  return name === 'fail' || Object.hasOwn(MOCK_SCENARIOS, name);
}

export function mockResult(name: keyof typeof MOCK_SCENARIOS): RawMeasurementResult {
  const s = MOCK_SCENARIOS[name];
  const engine: EngineRaw = {
    downloadMbps: s.downloadMbps,
    idleLatencyMs: s.latencyMs,
    idleJitterMs: s.jitterMs,
    loadedLatencyMs: s.loadedLatencyMs,
    loadedJitterMs: s.loadedLatencyMs === null ? null : s.jitterMs * 3,
    idleLatencySamples: 20,
    loadedLatencySamples: s.loadedLatencyMs === null ? 0 : 12,
    downloadBytes: Math.round(s.downloadMbps * 1e6 * 0.6),
    durationMs: 6000,
    protocols: ['mock'],
  };
  return {
    provider: `mock:${name}`,
    endpoint: 'mock',
    startedAt: new Date(0).toISOString(),
    durationMs: 11500,
    engine,
    upload: {
      mbps: s.uploadMbps,
      attributedBytes: (s.uploadMbps * 1e6 * 4) / 8,
      bytesSent: (s.uploadMbps * 1e6 * 5) / 8,
      requests: 24,
      maxChunkBytes: 4 * 1024 * 1024,
      durationMs: 5300,
      protocols: ['mock'],
      errors: [],
      timeline: [],
    },
  };
}

const STAGES: TestStage[] = ['connecting', 'latency', 'download', 'upload'];

export class MockProvider implements MeasurementProvider {
  readonly name: string;

  constructor(
    private readonly scenario: MockScenarioName,
    private readonly stepMs = 700,
  ) {
    this.name = `mock:${scenario}`;
  }

  async startTest(options: TestOptions = {}): Promise<RawMeasurementResult> {
    const { onStage, signal } = options;
    for (const stage of STAGES) {
      onStage?.(stage);
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, this.stepMs);
        signal?.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(signal.reason);
          },
          { once: true },
        );
      });
      if (this.scenario === 'fail' && stage === 'upload') {
        throw new MeasurementError('network', 'Simulated failure (mock)', 'upload');
      }
    }
    return mockResult(this.scenario === 'fail' ? 'excellent' : this.scenario);
  }
}
